import { Link } from 'react-router-dom';
import { ChevronRight, type LucideIcon } from 'lucide-react';
import {
  getCategory,
  getToolByIds,
  getToolsByCategory,
  type ToolCategoryId,
} from '../registry';

/**
 * Shared header for every page under `/tools/:category/:tool`.
 *
 * Solves three nav-orientation gripes in one place:
 *   1. There's no way back: the title strip used to render only an
 *      icon + name + path badge, with no link to either the category
 *      or the toolbox overview. Now the breadcrumb does both.
 *   2. There's no way sideways: jumping from `url-parse` to
 *      `http-status` required hitting the sidebar (or, worse,
 *      remembering the URL). The "同类工具" chip strip surfaces every
 *      sibling tool in the current category as a one-click link.
 *   3. The same header is duplicated across 11 tool pages. Keeping it
 *      in one place means future polish (mobile collapse, hotkey
 *      hints, share button) lands in one file instead of eleven.
 *
 * Required props are the bare minimum the page already carries; the
 * registry supplies the rest (icon, category label, sibling list).
 * If the (toolId, category) pair somehow misses the registry — e.g.
 * during a refactor — we fall back to a minimal title-only header so
 * the page at least renders instead of crashing.
 */
export interface ToolHeaderProps {
  /** Tool id from `web/src/tools/registry.ts`, e.g. `'url-parse'`. */
  toolId: string;
  /** Owning category id, e.g. `'network'`. */
  category: ToolCategoryId;
  /** One-line description shown under the title. Often longer than registry's. */
  description: string;
  /**
   * Optional override for the toolbar slot at the top-right of the
   * header (e.g. global format / clear buttons that some tools want
   * up here instead of in their own card-pad). Most tools don't
   * supply this.
   */
  toolbar?: React.ReactNode;
}

export function ToolHeader({
  toolId,
  category,
  description,
  toolbar,
}: ToolHeaderProps): JSX.Element {
  const cat = getCategory(category);
  const tool = getToolByIds(category, toolId);

  // Defensive: registry typo / mid-refactor → render the minimum
  // viable header so the page itself still works.
  if (!cat || !tool) {
    return (
      <header className="flex flex-col gap-1">
        <h1 className="text-lg font-semibold tracking-tight text-ink">{toolId}</h1>
        <p className="text-[12px] text-ink-muted">{description}</p>
      </header>
    );
  }

  const Icon = tool.icon;
  const siblings = getToolsByCategory(category)
    .filter((t) => t.id !== toolId && t.status === 'available');

  return (
    <header className="flex flex-col gap-2">
      {/* Breadcrumb — always rendered so "where am I / how do I go back" is one glance. */}
      <nav
        aria-label="面包屑"
        className="flex flex-wrap items-center gap-1 text-[11px] text-ink-dim"
      >
        <Link
          to="/tools"
          className="rounded px-1 hover:bg-bg-soft hover:text-accent"
        >
          工具广场
        </Link>
        <ChevronRight className="h-3 w-3" />
        <Link
          to={`/tools/${cat.id}`}
          className="inline-flex items-center gap-1 rounded px-1 hover:bg-bg-soft hover:text-accent"
        >
          {cat.shortLabel}
        </Link>
        <ChevronRight className="h-3 w-3" />
        <span className="px-1 text-ink-muted">{tool.name}</span>
      </nav>

      {/* Title + path badge + optional toolbar. */}
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className="h-5 w-5 shrink-0 text-accent" />
          <h1 className="text-lg font-semibold tracking-tight text-ink truncate">
            {tool.name}
          </h1>
          <span className="badge font-mono">{tool.path}</span>
        </div>
        {toolbar && <div className="flex shrink-0 items-center gap-1.5">{toolbar}</div>}
      </div>

      {/* Description. */}
      <p className="text-[12px] leading-relaxed text-ink-muted">{description}</p>

      {/* Sibling chip strip — only when there's at least one other available tool. */}
      {siblings.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] text-ink-dim">同类工具：</span>
          {siblings.map((s) => (
            <SiblingChip key={s.id} icon={s.icon} label={s.name} to={s.path} />
          ))}
          <Link
            to={`/tools/${cat.id}`}
            className="inline-flex items-center gap-1 rounded border border-dashed border-line px-1.5 py-0.5 text-[11px] text-ink-dim hover:border-accent/50 hover:text-accent"
            title={`查看 ${cat.shortLabel} 全部工具`}
          >
            全部
          </Link>
        </div>
      )}
    </header>
  );
}

function SiblingChip({
  icon: Icon,
  label,
  to,
}: {
  icon: LucideIcon;
  label: string;
  to: string;
}): JSX.Element {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1 rounded border border-line bg-bg-card px-1.5 py-0.5 text-[11px] text-ink-muted transition hover:border-accent/50 hover:text-accent"
    >
      <Icon className="h-3 w-3" />
      <span>{label}</span>
    </Link>
  );
}
